import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../components/contentwrapper/ContentWrapper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../utils/withAuth";
import { fetchAllDataByIDAPI, getSingleDataFromDatabase } from "../utils/api";
import { FaFacebookF, FaGoogle } from "react-icons/fa";
import { RiAdvertisementFill } from "react-icons/ri";
import Skeleton from "react-loading-skeleton";
import DateRangePicker from "react-bootstrap-daterangepicker";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    ComposedChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";
import moment from "moment";
import { numberFormatWithGst } from "../components/numberFormatWithGst";

const Dashboard = () => {
    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");
    const defaultStart = useMemo(() => moment().startOf("month"), []);
    const defaultEnd = useMemo(() => moment().endOf("day"), []);
    const [dateRange, setDateRange] = useState({
        startDate: defaultStart,
        endDate: defaultEnd,
    });
    const drpRef = useRef(null);
    const [ppc, setPPC] = useState({});
    const [currentTime, setCurrentTime] = useState(new Date());

    const dateLabel = useMemo(() => {
        const s = dateRange.startDate?.format("DD MMM YYYY");
        const e = dateRange.endDate?.format("DD MMM YYYY");
        return `${s} - ${e}`;
    }, [dateRange]);

    // spend cards
    const [isLoading, setIsLoading] = useState(false);
    const [spendData, setSpendData] = useState(null);

    // charts + tables from API
    const [chartData, setChartData] = useState({
        amountByDate: [],
        imprClicksByDate: [],
    });
    const [cpcMetricsByDate, setCpcMetricsByDate] = useState([]);
    const [sourceRows, setSourceRows] = useState([]);
    const [campaignSummaryRows, setCampaignSummaryRows] = useState([]);

    // 1) Always-running clock
    useEffect(() => {
        const t = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // 2) Load PPC basic data
    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/dashboard");
        (async () => {
            try {
                const res = await getSingleDataFromDatabase(
                    `ppc-get-single-data/${ppcid}`
                );
                if (res?.data?.status === 1) setPPC(res.data.ppcdata || {});
                else toast.error(res?.data?.message || "Failed to load PPC data");
            } catch (e) {
                console.error(e);
                toast.error("Network/CORS error while loading PPC data");
            }
        })();
    }, [ppcid, navigate]);

    const formattedTime = currentTime.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    const heroDate = currentTime.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });

    const greeting = () => {
        const h = currentTime.getHours();
        if (h < 12) return "Good Morning";
        if (h < 17) return "Good Afternoon";
        return "Good Evening";
    };

    const defaultSources = [
        { key: "google", label: "Google Ads", icon: <FaGoogle /> },
        { key: "facebook", label: "Facebook Ads", icon: <FaFacebookF /> },
        { key: "others", label: "Others", icon: <RiAdvertisementFill /> },
    ];

    const sections = [
        {
            key: "today",
            label: "Today's Spend",
            accentClass: "border-start border-4 border-primary",
            accent: "#2ea8ff",
        },
        {
            key: "yesterday",
            label: "Yesterday's Spend",
            accentClass: "border-start border-4 border-success",
            accent: "#22c55e",
        },
        {
            key: "thisMonth",
            label: "This Month Spend",
            accentClass: "border-start border-4 border-warning",
            accent: "#facc15",
        },
        {
            key: "lastMonth",
            label: "Last Month Spend",
            accentClass: "border-start border-4 border-info",
            accent: "#8b5cf6",
        },
        {
            key: "overall",
            label: "Total Spend",
            accentClass: "border-start border-4 border-danger",
            accent: "#fb7185",
        },
    ];

    // ---------------------- LOAD DASHBOARD SPEND + METRICS -------------------
    useEffect(() => {
        if (!ppcid) return;
        loadSpend(defaultStart, defaultEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const loadSpend = (start = dateRange.startDate, end = dateRange.endDate) => {
        setIsLoading(true);

        const startStr = moment(start).format("YYYY-MM-DD");
        const endStr = moment(end).format("YYYY-MM-DD");

        fetchAllDataByIDAPI(
            `ppc-team-get-dashboard-spend-with-source-name/${ppcid}?start_date=${startStr}&end_date=${endStr}`
        )
            .then((res) => {
                if (res?.data?.status === 1) {
                    const api = res.data.data || {};

                    // ✅ NEW: cards object from API
                    const cards = api.cards || {};

                    const formatRangeLabel = (start, end) => {
                        if (!start && !end) return "";

                        const fmt = (d) =>
                            new Date(d).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            });

                        if (!start || !end) return start || end || "";
                        if (start === end) return fmt(start);
                        return `${fmt(start)} – ${fmt(end)}`;
                    };

                    // ✅ read sections from cards.<key> instead of api.<key>
                    const mapSec = (keyApi) => {
                        const sec = cards[keyApi] || {};
                        return {
                            label: sec.label || "",
                            dateLabel: formatRangeLabel(
                                sec.date_start || null,
                                sec.date_end || null
                            ),
                            total: Number(sec.total) || 0,
                            sources: sec.sources || [],
                        };
                    };

                    // ---- cards data ----
                    setSpendData({
                        today: mapSec("today"),
                        yesterday: mapSec("yesterday"),
                        thisMonth: mapSec("this_month"),
                        lastMonth: mapSec("last_month"),
                        overall: mapSec("overall"),
                    });

                    // ---- charts data ----
                    const charts = api.charts || {};
                    const amountRows = charts.amount_by_date?.rows || [];
                    const metricRows =
                        charts.impressions_clicks_by_date?.rows || [];

                    const shortDate = (d) =>
                        new Date(d).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                        });

                    const amountByDate = amountRows.map((r) => ({
                        date: shortDate(r.date),
                        spend: Number(r.total_spend) || 0,
                    }));

                    const imprClicksByDate = metricRows.map((r) => ({
                        date: shortDate(r.date),
                        impressions: Number(r.impressions) || 0,
                        clicks: Number(r.clicks) || 0,
                    }));

                    setChartData({
                        amountByDate,
                        imprClicksByDate,
                    });

                    // ---- CPC by date (overall) ----
                    const metricsByDateMap = {};
                    metricRows.forEach((r) => {
                        metricsByDateMap[r.date] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                        };
                    });

                    const metricRowsByDate = {};
                    metricRows.forEach((r) => {
                        metricRowsByDate[r.date] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                        };
                    });

                    const rows = amountRows.map((r) => {
                        const m = metricRowsByDate[r.date] || {};
                        const spend = Number(r.total_spend) || 0;
                        const clicks = m.clicks || 0;
                        const impressions = m.impressions || 0;

                        const cpc = clicks > 0 ? Number((spend / clicks).toFixed(2)) : 0;
                        const cpm = impressions > 0 ? Number(((spend / impressions) * 1000).toFixed(2)) : 0;

                        return {
                            period: shortDate(r.date),
                            cpc,
                            cpm,
                            impressions,
                        };
                    });

                    setCpcMetricsByDate(rows);

                    // ---- source performance ----
                    const srcPerf = api.source_performance || [];

                    /* 1️⃣ Convert API data into lookup map */
                    const perfMap = {};
                    srcPerf.forEach((r) => {
                        const key = (r.source_name || "").toLowerCase().trim();
                        perfMap[key] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                            amount: Number(r.amount_spent) || 0,
                        };
                    });

                    /* 2️⃣ Always build table rows from defaultSources */
                    const normalizedSources = defaultSources.map((src) => {
                        const data = perfMap[src.key] || {
                            impressions: 0,
                            clicks: 0,
                            amount: 0,
                        };

                        const impressions = data.impressions;
                        const clicks = data.clicks;
                        const amount = data.amount;

                        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                        const cpc = clicks > 0 ? amount / clicks : 0;
                        const cpm = impressions > 0 ? (amount / impressions) * 1000 : 0;

                        return {
                            source: src.label, 
                            impressions,
                            clicks,
                            ctr: Number(ctr.toFixed(2)),
                            amount,
                            cpc: Number(cpc.toFixed(2)),
                            cpm: Number(cpm.toFixed(2)),
                        };
                    });

                    setSourceRows(normalizedSources);

                    // ---- campaign summary table ----
                    const summary = api.summary_table || [];
                    const normalizedSummary = summary.map((r, idx) => ({
                        name: r.skill_name || `Campaign ${idx + 1}`,
                        type: r.level || "",
                        source: r.source_name || "",
                        amount: Number(r.amount_spent) || 0,
                        impressions: Number(r.impressions) || 0,
                        clicks: Number(r.clicks) || 0,
                        ctr: Number(r.ctr) || 0,
                        cpc: Number(r.cpc) || 0,
                        cpm: Number(r.cpm) || 0,
                    }));
                    setCampaignSummaryRows(normalizedSummary);
                }
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsLoading(false);
            });
    };

    const formatCurrency = (v) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(v || 0);

    const getSafeSources = (raw, total) => {
        const map = {};
        raw?.forEach((s) => {
            const key = (s.source_name || "").toLowerCase();
            map[key] = Number(s.spend) || 0;
        });

        return defaultSources.map((src) => {
            const amt = map[src.key] || 0;
            const pct = total > 0 ? ((amt / total) * 100).toFixed(1) : "0.0";
            return { ...src, amount: amt, pct };
        });
    };

    const renderCard = (section) => {
        if (isLoading || !spendData) {
            return (
                <div className="col" key={section.key}>
                    <div className="premium-spend-card premium-spend-card--loading">
                        <Skeleton width={120} height={14} className="mb-2" />
                        <Skeleton width={80} height={26} className="mb-3" />
                        <Skeleton height={10} className="mb-2" />
                        <Skeleton height={10} className="mb-2" />
                        <Skeleton height={10} className="mb-2" />
                    </div>
                </div>
            );
        }

        const data = spendData[section.key];
        if (!data) return null;

        const safeSources = getSafeSources(data.sources, data.total);

        return (
            <div className="col" key={section.key}>
                <div
                    className={`premium-spend-card premium-spend-card--${section.key}`}
                    style={{ "--spend-accent": section.accent }}
                >
                    <div className="premium-spend-head">
                        <div>
                            <div className="premium-spend-label">{section.label}</div>
                            {data.dateLabel && (
                                <div className="premium-spend-date">{data.dateLabel}</div>
                            )}
                        </div>
                        <div className="premium-spend-total">{numberFormatWithGst(data.total)}</div>
                    </div>

                    <div className="premium-source-list">
                        {safeSources.map((src) => (
                            <div key={src.label} className="premium-source-row">
                                <div className="premium-source-meta">
                                    <span className="premium-source-icon">{src.icon}</span>
                                    <span className="premium-source-name">{src.label}</span>
                                    <span className="premium-source-amount">
                                        {numberFormatWithGst(src.amount)}
                                    </span>
                                </div>
                                <div className="premium-source-track">
                                    <span
                                        style={{
                                            width: `${Math.min(Number(src.pct) || 0, 100)}%`,
                                            background: `linear-gradient(90deg, #4d978f, ${section.accent})`,
                                        }}
                                    />
                                    <strong className={Number(src.pct) > 3 ? "is-filled" : "is-empty"}>
                                        {src.pct}%
                                    </strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // --------- small reusable legend with clickable items ----------
    const ChartLegend = ({ chartKey, items, visibility, onToggle }) => {
        return (
            <div className="chart-legend">
                {items.map((item) => {
                    const active = visibility?.[item.key];
                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`chart-legend-item ${active ? "active" : "inactive"
                                }`}
                            onClick={() => onToggle(chartKey, item.key)}
                        >
                            <span
                                className="chart-legend-dot"
                                style={{ backgroundColor: item.color }}
                            />
                            <span className="chart-legend-label">
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    // ---------------- visibility state for time-series charts -------------
    const [legendVisibility, setLegendVisibility] = useState({
        chart2: { impressions: true, clicks: true },
    });

    const handleToggleLegend = (chartKey, seriesKey) => {
        setLegendVisibility((prev) => ({
            ...prev,
            [chartKey]: {
                ...prev[chartKey],
                [seriesKey]: !prev[chartKey][seriesKey],
            },
        }));
    };

    // ---------------------- source table sorting + totals -----------------
    const [sortConfig, setSortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {sortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    const maxValues = useMemo(() => {
        if (!sourceRows.length) {
            return {
                impressions: 0,
                clicks: 0,
                ctr: 0,
                amount: 0,
                cpc: 0,
                cpm: 0,
            };
        }
        return {
            impressions: Math.max(...sourceRows.map((r) => r.impressions)),
            clicks: Math.max(...sourceRows.map((r) => r.clicks)),
            ctr: Math.max(...sourceRows.map((r) => r.ctr)),
            amount: Math.max(...sourceRows.map((r) => r.amount)),
            cpc: Math.max(...sourceRows.map((r) => r.cpc)),
            cpm: Math.max(...sourceRows.map((r) => r.cpm)),
        };
    }, [sourceRows]);

    const sortedSources = useMemo(() => {
        const rows = [...sourceRows];
        const { key, direction } = sortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [sortConfig, sourceRows]);

    const clamp01 = (t) => Math.min(1, Math.max(0, t));

    const getHeatStyle = (value, max, theme = "blue") => {
        // Themes: gold | blue | blue2 | mint | pink | rose
        const themes = {
            gold: { hue: 45, sat: 70, lightMin: 30, lightMax: 92 },
            blue: { hue: 215, sat: 75, lightMin: 25, lightMax: 92 },
            blue2: { hue: 200, sat: 75, lightMin: 25, lightMax: 92 },
            mint: { hue: 160, sat: 55, lightMin: 30, lightMax: 95 },
            pink: { hue: 300, sat: 65, lightMin: 35, lightMax: 93 },
            rose: { hue: 330, sat: 55, lightMin: 38, lightMax: 95 },
        };

        const cfg = themes[theme] || themes.blue;

        if (!max || max <= 0) {
            return { background: "transparent", color: "#111827" };
        }

        const t = value === 0 ? 0 : clamp01(value / max);
        const lightness = cfg.lightMax - (cfg.lightMax - cfg.lightMin) * t;

        // ✅ text auto contrast
        const isDark = lightness < 58;
        const textColor = isDark ? "#ffffff" : "#111827";

        return {
            background: `hsl(${cfg.hue}, ${cfg.sat}%, ${lightness}%)`,
            color: textColor,
            fontWeight: 700,

            // ✅ extra visibility on any bg (especially blue/pink)
            textShadow: isDark ? "0 1px 2px rgba(0,0,0,0.35)" : "0 1px 0 rgba(255,255,255,0.6)",
        };
    };

    const formatNumber = (n) => (n || 0).toLocaleString("en-US");
    const formatPercent = (n) => `${(n || 0).toFixed(2)}%`;

    const cpcSummary = useMemo(() => {
        const totals = cpcMetricsByDate.reduce(
            (acc, row) => {
                acc.cpc += Number(row.cpc) || 0;
                acc.cpm += Number(row.cpm) || 0;
                acc.impressions += Number(row.impressions) || 0;
                acc.rows += 1;
                return acc;
            },
            { cpc: 0, cpm: 0, impressions: 0, rows: 0 }
        );

        return {
            avgCpc: totals.rows ? totals.cpc / totals.rows : 0,
            avgCpm: totals.rows ? totals.cpm / totals.rows : 0,
            impressions: totals.impressions,
        };
    }, [cpcMetricsByDate]);

    const sourceTotals = useMemo(() => {
        const t = sourceRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr =
            t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0;
        const cpc = t.clicks > 0 ? t.amount / t.clicks : 0;
        const cpm =
            t.impressions > 0 ? (t.amount / t.impressions) * 1000 : 0;
        return { ...t, ctr, cpc, cpm };
    }, [sourceRows]);

    // -------------------- summary table sorting + totals ------------------
    const [summarySortConfig, setSummarySortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const handleSummarySort = (key) => {
        setSummarySortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const summaryMaxValues = useMemo(() => {
        if (!campaignSummaryRows.length) {
            return {
                amount: 0,
                impressions: 0,
                clicks: 0,
                ctr: 0,
                cpc: 0,
                cpm: 0,
            };
        }
        return {
            amount: Math.max(...campaignSummaryRows.map((r) => r.amount)),
            impressions: Math.max(
                ...campaignSummaryRows.map((r) => r.impressions)
            ),
            clicks: Math.max(...campaignSummaryRows.map((r) => r.clicks)),
            ctr: Math.max(...campaignSummaryRows.map((r) => r.ctr)),
            cpc: Math.max(...campaignSummaryRows.map((r) => r.cpc)),
            cpm: Math.max(...campaignSummaryRows.map((r) => r.cpm)),
        };
    }, [campaignSummaryRows]);

    const renderSummarySortArrow = (key) => {
        if (summarySortConfig.key !== key)
            return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {summarySortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    const sortedCampaignSummaryRows = useMemo(() => {
        const rows = [...campaignSummaryRows];
        const { key, direction } = summarySortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [summarySortConfig, campaignSummaryRows]);

    const summaryTotals = useMemo(() => {
        const t = campaignSummaryRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr =
            t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0;
        const cpc = t.clicks > 0 ? t.amount / t.clicks : 0;
        const cpm =
            t.impressions > 0 ? (t.amount / t.impressions) * 1000 : 0;
        return { ...t, ctr, cpc, cpm };
    }, [campaignSummaryRows]);

    // ============================= RENDER ===============================
    return (
        <div className="misDashboard premium-dashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <ContentWrapper>
                <div className="premium-dashboard-hero">
                    <div className="premium-hero-copy">
                        <div className="premium-eyebrow">PPC Panel</div>
                        <h1>
                            Welcome back, <span>{ppc?.ppc_name || "Team"}</span>
                        </h1>
                        <p>{greeting()}</p>
                    </div>
                    <div className="premium-hero-actions">
                        <div className="premium-clock-card">
                            <div className="premium-clock-top">
                                <div className="premium-clock-label">Live Clock</div>
                                <DateRangePicker
                                    initialSettings={{
                                        startDate: dateRange.startDate.toDate(),
                                        endDate: dateRange.endDate.toDate(),
                                        autoUpdateInput: false,
                                        showDropdowns: true,
                                        alwaysShowCalendars: true,
                                        locale: { format: "DD MMM YYYY" },
                                    }}
                                    onApply={(e, picker) => {
                                        const start = moment(picker.startDate);
                                        const end = moment(picker.endDate);

                                        setDateRange({ startDate: start, endDate: end });
                                        loadSpend(start, end);
                                    }}
                                >
                                    <button className="premium-date-btn" ref={drpRef} type="button">
                                        Filter
                                    </button>
                                </DateRangePicker>
                            </div>
                            <div className="premium-clock-time">{formattedTime}</div>
                            <div className="premium-clock-date">{heroDate}</div>
                            <div className="premium-live-row">
                                <span />
                                Live time
                            </div>
                        </div>
                    </div>
                </div>

                {/* TOP SPEND CARDS */}
                <div className="premium-spend-grid mb-3">
                    <div className="row row-cols-1 row-cols-lg-5 g-3">
                        {sections.map((s) => renderCard(s))}
                    </div>
                </div>

                {/* MAIN DASHBOARD BLOCKS */}
                <div className="row g-3 premium-dashboard-grid" style={{ marginBottom: "55px" }}>
                    {/* 1: Amount spent over time */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Amount spend by Date</h5>
                            </div>
                            {isLoading ? (
                                <Skeleton height={280} />
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={chartData.amountByDate} margin={{ top: 12, right: 14, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke="#d6dce5" strokeDasharray="4 5" />
                                        <XAxis
                                            dataKey="date"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: "#0f172a", fontSize: 12, fontWeight: 600 }}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: "#0f172a", fontSize: 12 }}
                                            tickFormatter={(v) => Number(v || 0).toLocaleString("en-IN", { notation: "compact" })}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                border: "1px solid #e2e8f0",
                                                borderRadius: 14,
                                                boxShadow: "0 16px 36px rgba(15, 23, 42, 0.14)",
                                            }}
                                            formatter={(value) => [numberFormatWithGst(value), "Spend"]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="spend"
                                            stroke="#22c55e"
                                            strokeWidth={3}
                                            dot={{
                                                r: 4,
                                                fill: "#fff",
                                                stroke: "#22c55e",
                                                strokeWidth: 2,
                                            }}
                                            activeDot={{ r: 6, fill: "#22c55e" }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* 2: Impressions vs Clicks */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header d-flex justify-content-between align-items-center">
                                <h5>Impressions and Clicks by Date</h5>
                                <ChartLegend
                                    chartKey="chart2"
                                    visibility={legendVisibility.chart2}
                                    onToggle={handleToggleLegend}
                                    items={[
                                        {
                                            key: "impressions",
                                            label: "Impressions",
                                            color: "#0ea5e9",
                                        },
                                        {
                                            key: "clicks",
                                            label: "Clicks",
                                            color: "#dc2626",
                                        },
                                    ]}
                                />
                            </div>

                            {isLoading ? (
                                <Skeleton height={280} />
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={chartData.imprClicksByDate} margin={{ top: 12, right: 14, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke="#d6dce5" strokeDasharray="4 5" />
                                        <XAxis
                                            dataKey="date"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: "#0f172a", fontSize: 12, fontWeight: 600 }}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: "#0f172a", fontSize: 12 }}
                                            tickFormatter={(v) => Number(v || 0).toLocaleString("en-IN", { notation: "compact" })}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                border: "1px solid #e2e8f0",
                                                borderRadius: 14,
                                                boxShadow: "0 16px 36px rgba(15, 23, 42, 0.14)",
                                            }}
                                            formatter={(value, name) => [formatNumber(Number(value) || 0), name]}
                                        />
                                        {legendVisibility.chart2.impressions && (
                                            <Line
                                                type="monotone"
                                                dataKey="impressions"
                                                stroke="#0ea5e9"
                                                strokeWidth={3}
                                                dot={{ r: 4, fill: "#fff", stroke: "#0ea5e9", strokeWidth: 2 }}
                                                activeDot={{ r: 6, fill: "#0ea5e9" }}
                                            />
                                        )}
                                        {legendVisibility.chart2.clicks && (
                                            <Line
                                                type="monotone"
                                                dataKey="clicks"
                                                stroke="#dc2626"
                                                strokeWidth={3}
                                                dot={{ r: 4, fill: "#fff", stroke: "#dc2626", strokeWidth: 2 }}
                                                activeDot={{ r: 6, fill: "#dc2626" }}
                                            />
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* 5: Summary table */}
                    <div className="col-12">
                        <div className="chart-card summary-table-card">
                            <div className="chart-card-header">
                                <h5>Summary table</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    {isLoading ? (
                                        <Skeleton height={260} />
                                    ) : (
                                        <table className="source-table">
                                            <thead>
                                                <tr>
                                                    <th>Campaign name</th>
                                                    <th>Campaign type</th>
                                                    <th>Source</th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "amount"
                                                            )
                                                        }
                                                    >
                                                        Amount spend{" "}
                                                        {renderSummarySortArrow(
                                                            "amount"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "impressions"
                                                            )
                                                        }
                                                    >
                                                        Impressions{" "}
                                                        {renderSummarySortArrow(
                                                            "impressions"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "clicks"
                                                            )
                                                        }
                                                    >
                                                        Clicks{" "}
                                                        {renderSummarySortArrow(
                                                            "clicks"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "ctr"
                                                            )
                                                        }
                                                    >
                                                        CTR{" "}
                                                        {renderSummarySortArrow(
                                                            "ctr"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "cpc"
                                                            )
                                                        }
                                                    >
                                                        CPC{" "}
                                                        {renderSummarySortArrow(
                                                            "cpc"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "cpm"
                                                            )
                                                        }
                                                    >
                                                        CPM{" "}
                                                        {renderSummarySortArrow(
                                                            "cpm"
                                                        )}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedCampaignSummaryRows.map(
                                                    (row) => (
                                                        <tr key={row.name}>
                                                            <td>{row.name}</td>
                                                            <td>{row.type}</td>
                                                            <td>
                                                                {row.source}
                                                            </td>
                                                            <td className="heat-cell" style={getHeatStyle(row.amount, summaryMaxValues.amount, "gold")}>
                                                                {numberFormatWithGst(row.amount)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.impressions, summaryMaxValues.impressions, "blue")}>
                                                                {formatNumber(row.impressions)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.clicks, summaryMaxValues.clicks, "blue2")}>
                                                                {formatNumber(row.clicks)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.ctr, summaryMaxValues.ctr, "mint")}>
                                                                {formatPercent(row.ctr)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.cpc, summaryMaxValues.cpc, "pink")}>
                                                                {formatCurrency(row.cpc)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.cpm, summaryMaxValues.cpm, "rose")}>
                                                                {formatCurrency(row.cpm)}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={3}>
                                                        Grand total
                                                    </td>
                                                    <td>
                                                        {numberFormatWithGst(
                                                            summaryTotals.amount
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            summaryTotals.impressions
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            summaryTotals.clicks
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatPercent(
                                                            summaryTotals.ctr
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            summaryTotals.cpc
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            summaryTotals.cpm
                                                        )}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3: Source performance table */}
                    <div className="col-md-6">
                        <div className="chart-card source-table-card">
                            <div className="chart-card-header">
                                <h5>Source performance</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    {isLoading ? (
                                        <Skeleton height={220} />
                                    ) : (
                                        <table className="source-table">
                                            <thead>
                                                <tr>
                                                    <th>Source</th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort(
                                                                "impressions"
                                                            )
                                                        }
                                                    >
                                                        Impressions{" "}
                                                        {renderSortArrow(
                                                            "impressions"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("clicks")
                                                        }
                                                    >
                                                        Clicks{" "}
                                                        {renderSortArrow(
                                                            "clicks"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("ctr")
                                                        }
                                                    >
                                                        CTR{" "}
                                                        {renderSortArrow("ctr")}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("amount")
                                                        }
                                                    >
                                                        Amount spend{" "}
                                                        {renderSortArrow(
                                                            "amount"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("cpc")
                                                        }
                                                    >
                                                        CPC{" "}
                                                        {renderSortArrow("cpc")}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("cpm")
                                                        }
                                                    >
                                                        CPM{" "}
                                                        {renderSortArrow("cpm")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedSources.map((row) => (
                                                    <tr key={row.source}>
                                                        <td>{row.source}</td>

                                                        {/* ✅ Impressions */}
                                                        <td className="heat-cell" style={getHeatStyle(row.impressions, maxValues.impressions, "blue")}>
                                                            {formatNumber(row.impressions)}
                                                        </td>

                                                        {/* ✅ Clicks */}
                                                        <td className="heat-cell" style={getHeatStyle(row.clicks, maxValues.clicks, "blue2")}>
                                                            {formatNumber(row.clicks)}
                                                        </td>

                                                        {/* ✅ CTR */}
                                                        <td className="heat-cell" style={getHeatStyle(row.ctr, maxValues.ctr, "mint")}>
                                                            {formatPercent(row.ctr)}
                                                        </td>

                                                        {/* ✅ Amount spent */}
                                                        <td className="heat-cell" style={getHeatStyle(row.amount, maxValues.amount, "gold")}>
                                                            {numberFormatWithGst(row.amount)}
                                                        </td>

                                                        {/* ✅ CPC */}
                                                        <td className="heat-cell" style={getHeatStyle(row.cpc, maxValues.cpc, "pink")}>
                                                            {formatCurrency(row.cpc)}
                                                        </td>

                                                        {/* ✅ CPM */}
                                                        <td className="heat-cell" style={getHeatStyle(row.cpm, maxValues.cpm, "rose")}>
                                                            {formatCurrency(row.cpm)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td>Grand total</td>
                                                    <td>
                                                        {formatNumber(
                                                            sourceTotals.impressions
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            sourceTotals.clicks
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatPercent(
                                                            sourceTotals.ctr
                                                        )}
                                                    </td>
                                                    <td>
                                                        {numberFormatWithGst(
                                                            sourceTotals.amount
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            sourceTotals.cpc
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            sourceTotals.cpm
                                                        )}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 4: CPC by date (overall) */}
                    <div className="col-md-6">
                        <div className="chart-card" style={{ overflow: "hidden" }}>
                            <div className="chart-card-header d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div>
                                    <h5 className="mb-1">Cost efficiency by date</h5>
                                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 600 }}>
                                        Avg CPC, CPM and impressions trend
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <div style={{ background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 12, padding: "7px 10px", minWidth: 92 }}>
                                        <div style={{ color: "#2563eb", fontSize: 11, fontWeight: 800 }}>AVG CPC</div>
                                        <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 800 }}>{formatCurrency(cpcSummary.avgCpc)}</div>
                                    </div>
                                    <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: "7px 10px", minWidth: 92 }}>
                                        <div style={{ color: "#ea580c", fontSize: 11, fontWeight: 800 }}>AVG CPM</div>
                                        <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 800 }}>{formatCurrency(cpcSummary.avgCpm)}</div>
                                    </div>
                                    <div style={{ background: "#ecfeff", border: "1px solid #bae6fd", borderRadius: 12, padding: "7px 10px", minWidth: 112 }}>
                                        <div style={{ color: "#0284c7", fontSize: 11, fontWeight: 800 }}>IMPRESSIONS</div>
                                        <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 800 }}>{formatNumber(cpcSummary.impressions)}</div>
                                    </div>
                                </div>
                            </div>
                            {isLoading ? (
                                <Skeleton height={300} />
                            ) : (
                                <div style={{ width: "100%", overflowX: "auto", paddingTop: 8 }}>
                                    <div style={{ minWidth: Math.max(560, cpcMetricsByDate.length * 58), height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart
                                                data={cpcMetricsByDate}
                                                margin={{ top: 18, right: 10, left: 0, bottom: 4 }}
                                                barCategoryGap="28%"
                                            >
                                                <defs>
                                                    <linearGradient id="impressionSoftFill" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.28} />
                                                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.02} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 6" vertical={false} />
                                                <XAxis
                                                    dataKey="period"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: "#475569", fontSize: 12, fontWeight: 600 }}
                                                    interval={0}
                                                />
                                                <YAxis
                                                    yAxisId="cost"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: "#64748b", fontSize: 12 }}
                                                    tickFormatter={(v) => `₹${v}`}
                                                    width={42}
                                                />
                                                <YAxis
                                                    yAxisId="impressions"
                                                    orientation="right"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: "#64748b", fontSize: 12 }}
                                                    tickFormatter={(v) => Number(v || 0).toLocaleString("en-IN", { notation: "compact" })}
                                                    width={52}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
                                                    contentStyle={{
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: 14,
                                                        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.14)",
                                                    }}
                                                    formatter={(value, name) => {
                                                        if (name === "Impressions") return [Number(value || 0).toLocaleString("en-IN"), name];
                                                        return [formatCurrency(Number(value || 0)), name];
                                                    }}
                                                />
                                                <Legend
                                                    verticalAlign="top"
                                                    align="left"
                                                    iconType="circle"
                                                    wrapperStyle={{ paddingBottom: 10, fontSize: 12, fontWeight: 700 }}
                                                />
                                                <Area
                                                    yAxisId="impressions"
                                                    type="monotone"
                                                    dataKey="impressions"
                                                    name="Impressions"
                                                    stroke="#0ea5e9"
                                                    strokeWidth={2}
                                                    fill="url(#impressionSoftFill)"
                                                    dot={false}
                                                    activeDot={{ r: 4 }}
                                                />
                                                <Bar
                                                    yAxisId="cost"
                                                    dataKey="cpc"
                                                    name="Avg CPC"
                                                    fill="#2563eb"
                                                    radius={[8, 8, 0, 0]}
                                                    maxBarSize={18}
                                                />
                                                <Bar
                                                    yAxisId="cost"
                                                    dataKey="cpm"
                                                    name="CPM"
                                                    fill="#f59e0b"
                                                    radius={[8, 8, 0, 0]}
                                                    maxBarSize={18}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(Dashboard);
